import { useEffect, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { PERSONAS, type PersonaId } from "./constants";
import { getJob } from "./api";
import type { AmbJobPublic } from "./types";

/**
 * The output screen for a MULTI-VOICE run: downloads, and nothing else.
 *
 * OWNER DIRECTIVE, 2026-08-04, and the reasoning is theirs:
 *
 *   "the risk i see with this is when the on page report shows up before downloading the pdf and
 *    word document dont show all the narratives in this situation, in fact when multiple
 *    narratives are selected dont show anything on screen besides the option to download all the
 *    files individually or as a zip downloading all pdfs and docx files in the zip"
 *
 * That is the right call and worth stating plainly: an inline report can only ever show ONE
 * narrative, so rendering it beside a set of five would silently promote one voice to "the"
 * report — the agent would read that one, download all five, and have no idea which file they had
 * actually reviewed. Showing nothing is the honest option, because the comparison IS the
 * deliverable here.
 *
 * NO CROSS CONTAMINATION, STRUCTURALLY. Each voice is a separate `AmbJob` with its own
 * `data_json`, written by its own build from the same confirmed inputs. This component only reads
 * job status and links to per-job downloads; it never merges, re-renders or re-runs anything.
 *
 * A voice that failed is NAMED. A short set presented as complete is the failure mode that
 * matters when the whole point is to compare five things.
 */
const POLL_MS = 3000;

export function PersonaDownloads({
  jobs,
  onStartOver,
  onBackToReview,
  editedSinceBuild,
  buildUnverifiable,
}: {
  jobs: { persona: PersonaId; jobId: string }[];
  onStartOver: () => void;
  /** THE FILES BELOW PREDATE A CHANGE THE AGENT HAS SINCE MADE (2026-08-07). Computed by
   * `CmaAmbFlow` — see `editedSinceBuild` there. Nothing on this screen starts a build, so there is
   * no control to withdraw; what there is instead is a set of download links that look current and
   * are not, so it is said in words. The exit is already here: "Back to review". */
  editedSinceBuild?: boolean;
  /** The page was reopened after a reload and the payload that built these files was not stored
   * (it held MLS rows that are never saved), so whether anything changed since cannot be checked.
   * Said in words rather than dropping the notice above. */
  buildUnverifiable?: boolean;
  /** NAVIGATION rule 6 — "no dead-ends" (2026-08-07, owner request: "the ability to go back to any
   * of the steps in the workflow ... without rerunning the entire workflow"). This screen offered
   * exactly one exit, and it was the most expensive move available: start over with a different
   * file. An agent comparing five voices who spots a wrong comparable needs the review, not a fresh
   * upload. Omitted, not disabled, when there is no review to return to — the same rule
   * `NarrativeReportScreen`'s own back follows. */
  onBackToReview?: () => void;
}) {
  const [state, setState] = useState<Record<string, AmbJobPublic | null>>({});

  useEffect(() => {
    let live = true;
    const tick = async () => {
      const next: Record<string, AmbJobPublic | null> = {};
      await Promise.all(
        jobs.map(async ({ jobId }) => {
          try {
            next[jobId] = await getJob(jobId);
          } catch {
            next[jobId] = null;
          }
        })
      );
      if (live) setState(next);
    };
    void tick();
    const t = setInterval(tick, POLL_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [jobs]);

  const rows = jobs.map((j) => ({ ...j, job: state[j.jobId] ?? null }));
  const done = rows.filter((r) => r.job?.status === "done");
  const failed = rows.filter((r) => r.job?.status === "failed" || r.job?.status === "needs_input");
  const working = rows.length - done.length - failed.length;
  const allSettled = working === 0;
  const zipHref = done.length
    ? `/api/amb/bundle.zip?ids=${done.map((r) => r.jobId).join(",")}`
    : null;

  const name = (id: PersonaId) => PERSONAS.find((p) => p.id === id)?.name ?? id;

  return (
    <section
      aria-labelledby="vb-persona-downloads"
      style={{ marginTop: 20, background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 22 }}
    >
      <h2 id="vb-persona-downloads" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
        {allSettled ? "Your reports" : "Writing your reports"}
      </h2>
      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.55 }}>
        {rows.length} voices, each written and rendered on its own from the same confirmed figures.
        The report is not shown on screen for a comparison set — an inline report can only show one
        narrative, and putting one of five on the page would make it look like the report.
        {working > 0 ? ` ${working} still writing.` : ""}
      </p>

      {allSettled && done.length === 0 && rows.length > 0 && (
        // NONE OF THEM FINISHED (2026-08-08). The heading flips to "Your reports" the moment
        // nothing is still writing — including when nothing succeeded — so a 0-for-5 run announced
        // itself as complete. Every voice is built from the SAME confirmed figures, so five
        // identical outcomes is one cause, not five; the reasons beneath say which.
        <p
          role="status"
          style={{
            marginTop: 12, marginBottom: 0, padding: "12px 14px", borderRadius: 10,
            background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
            fontSize: 13, lineHeight: 1.6, color: colors.textPrimary,
          }}
        >
          None of the {rows.length} voices finished. They are built from the same confirmed figures
          and differ only in wording, so this is one cause rather than {rows.length} — the reason
          under each voice below says which. Nothing you entered was lost: “Back to review” still
          has it.
        </p>
      )}

      {!editedSinceBuild && buildUnverifiable && (
        <p
          role="status"
          style={{
            marginTop: 12, marginBottom: 0, padding: "12px 14px", borderRadius: 10,
            background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
            fontSize: 13, lineHeight: 1.6, color: colors.textPrimary,
          }}
        >
          This page was reopened after a reload, so we can&apos;t check whether you changed something
          since these were written. If you did, every file here still carries the earlier figures. Go
          back to the review and generate again to be sure.
        </p>
      )}

      {editedSinceBuild && (
        <p
          role="status"
          style={{
            marginTop: 12, marginBottom: 0, padding: "12px 14px", borderRadius: 10,
            background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
            fontSize: 13, lineHeight: 1.6, color: colors.textPrimary,
          }}
        >
          You have changed something since these were written, so every file here still carries the
          earlier figures. Go back to the review and generate again to pick your change up.
        </p>
      )}

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r) => {
          const st = r.job?.status;
          const ready = st === "done";
          // NEEDS_INPUT IS NOT A FAILURE (split 2026-08-08). These were one branch, and the branch
          // said "didn't finish". A job at `needs_input` has not failed — it is waiting on the
          // agent, and telling them it died is how a thirty-second fix becomes a support ticket.
          const asking = st === "needs_input";
          const bad = st === "failed" || asking;
          // THE SERVER ALWAYS SENT THIS AND THIS SCREEN ALWAYS DROPPED IT (2026-08-08).
          // `_job_public` puts `reason` on every poll and `types.ts` declares it; the row rendered
          // one fixed sentence instead. A five-voice run that failed five times therefore produced
          // five identical shrugs and no diagnosis — including, verifiably, the reaper's own
          // "This job was interrupted when the server restarted", which names the cause outright.
          // A stated outcome is the whole difference between "the product is broken" and a fix.
          const why = (r.job?.reason || "").trim();
          return (
            <div
              key={r.jobId}
              style={{
                display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
                padding: "10px 12px", borderRadius: 8, border: `1px solid ${colors.border}`,
                background: colors.bgApp,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, flex: "0 0 auto", paddingTop: 1 }}>
                {name(r.persona)}
              </span>
              {ready ? (
                <span style={{ display: "flex", gap: 10 }}>
                  <a href={`/api/amb/jobs/${r.jobId}/download?fmt=pdf`} style={{ fontSize: 13, color: colors.accent }}>
                    PDF
                  </a>
                  <a href={`/api/amb/jobs/${r.jobId}/download?fmt=docx`} style={{ fontSize: 13, color: colors.accent }}>
                    Word
                  </a>
                </span>
              ) : bad ? (
                // Right-aligned and wrapping, because a real reason is a sentence and not a chip.
                <span style={{ fontSize: 12, color: colors.caution, textAlign: "right", lineHeight: 1.5 }}>
                  <strong style={{ fontWeight: 600 }}>
                    {asking ? "needs something from you" : "didn't finish"}
                  </strong>
                  {" — the other voices are unaffected"}
                  {why && (
                    <span style={{ display: "block", marginTop: 3, color: colors.textSecondary }}>
                      {why}
                    </span>
                  )}
                  {!why && (
                    <span style={{ display: "block", marginTop: 3, color: colors.textSecondary }}>
                      No reason was recorded. Job id {r.jobId} — that id is what support needs.
                    </span>
                  )}
                </span>
              ) : (
                <span style={{ fontSize: 12, color: colors.textSecondary }}>writing…</span>
              )}
            </div>
          );
        })}
      </div>

      {zipHref && (
        <a
          href={zipHref}
          style={{
            display: "inline-block", marginTop: 16, padding: "11px 20px", borderRadius: 8,
            background: colors.verified, color: "#fff", fontWeight: 600, fontSize: 14,
            textDecoration: "none",
          }}
        >
          Download all {done.length === rows.length ? rows.length : done.length} as a ZIP
        </a>
      )}
      {zipHref && done.length < rows.length && (
        <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12, color: colors.caution }}>
          The ZIP holds the {done.length} that finished. Every file is named with its voice, so
          nothing in it is ambiguous.
        </p>
      )}

      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {onBackToReview && (
          <button className="vb-btn"
            type="button"
            onClick={onBackToReview}
            style={{
              padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer",
            }}
          >
            Back to review
          </button>
        )}
        <button className="vb-btn"
          type="button"
          onClick={onStartOver}
          style={{
            padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer",
          }}
        >
          Start a new analysis
        </button>
      </div>
    </section>
  );
}
